import { ColumnDef, SortDirection as TanstackSortDir, SortingFnOption } from "@tanstack/react-table"
import { SortDirection, TableColumn, TableRowData, TableSortingType } from "./types"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"

// Map our <Table /> column definitions to '@tanstack/react-table' column definitions
export const mapColumnDefs = <TRowData extends TableRowData>(
    columnsData: TableColumn<TRowData>[],
    tableData: TRowData[]
): ColumnDef<TRowData>[] => {
    const columns: ColumnDef<TRowData>[] = columnsData.map(c => {
        const isWidthPercentage = typeof c.width === "string" && c.width.endsWith("%")
        return {
            id: c.id,
            accessorFn: c.getData,
            cell: props => c.cell(props.row.original, props.row.index),
            header: mapHeaderDef(c.header, tableData),
            footer: mapFooterDef(c.footer, tableData),
            columns: c.columns ? mapColumnDefs(c.columns, tableData) : undefined,
            size: isWidthPercentage ? undefined : (c.width as number),
            enableSorting: !!c.sorting,
            sortingFn: mapSortingDef(c.sorting) ?? undefined,
            sortDescFirst: c.sortDescFirst,
            meta: {
                align: c.align,
                columnClassName: c.columnClassName,
                filterComponent: c.filterComponent,
                widthPercentage: isWidthPercentage ? (c.width as string) : undefined,
                maxWidth: c.maxWidth,
            },
        }
    })

    return columns
}

const mapHeaderDef = <TRowData extends TableRowData>(
    headerDef: TableColumn<TRowData>["header"],
    tableData: TRowData[]
): ColumnDef<TRowData>["header"] => {
    if (headerDef && typeof headerDef === "function") {
        return () => headerDef(tableData)
    } else if (headerDef) {
        return () => headerDef
    }
}

const mapFooterDef = <TRowData extends TableRowData>(
    footerDef: TableColumn<TRowData>["footer"],
    tableData: TRowData[]
): ColumnDef<TRowData>["footer"] => {
    if (footerDef && typeof footerDef === "function") {
        return () => footerDef(tableData)
    } else if (footerDef) {
        return () => footerDef
    }
}

const mapSortingDef = <TRowData extends TableRowData>(
    sortingFn: Maybe<TableSortingType<TRowData>>
): Maybe<SortingFnOption<TRowData>> => {
    if (sortingFn && typeof sortingFn === "function") {
        return (rowA, rowB) => sortingFn(rowA.original, rowB.original)
    } else if (sortingFn) {
        return sortingFn
    }
}

export const mapSortDirection = (direction: false | TanstackSortDir): Maybe<SortDirection> => {
    switch (direction) {
        case "asc":
            return SortDirection.asc
        case "desc":
            return SortDirection.desc
        default:
            return null
    }
}

export const hasTableAnySelectableRows = (data: TableRowData[]): boolean => {
    return data.some(d => d.isSelectable || (d.children && hasTableAnySelectableRows(d.children)))
}

export const PAGINATION_WINDOW_SIZE = 5
export const PAGINATION_GAP = "..." as const

export const getPaginationPageNumbers = (
    currentPage: number,
    pageCount: number
): (number | typeof PAGINATION_GAP)[] => {
    const windowSize = PAGINATION_WINDOW_SIZE

    if (pageCount <= windowSize + 2) {
        return Array.from({ length: pageCount }, (_, i) => i + 1)
    }

    let start = currentPage - 2
    let end = currentPage + 2

    if (start < 1) {
        end += 1 - start
        start = 1
    }
    if (end > pageCount) {
        start -= end - pageCount
        end = pageCount
    }
    start = Math.max(start, 1)

    const pages: (number | typeof PAGINATION_GAP)[] = []

    if (start > 1) {
        pages.push(1)
        if (start > 2) {
            pages.push(PAGINATION_GAP)
        }
    }
    for (let page = start; page <= end; page++) {
        pages.push(page)
    }
    if (end < pageCount) {
        if (end < pageCount - 1) {
            pages.push(PAGINATION_GAP)
        }
        pages.push(pageCount)
    }

    return pages
}
